import EmptyState from '@components/Common/EmptyState';
import Panel from '@components/Common/Panel';
import MintlyTable from '@components/Common/Table';
import { isHrRole, isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import { useHrUsers } from '@components/Common/Utils/useHrUsers';
import { statusSummary } from '@constants/applicationStatus';
import { followUpsDueQuery } from '@constants/graphQLQueries';
import { candidateDisplayName } from '@helpers/candidateRecord';
import dayjs from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './index.scss';
import { formatDayMonth } from '@helpers/date';

interface Row {
  id: string;
  candidate: string;
  job: string;
  status: string;
  nextAction: string;
  due: string;
  overdue: boolean;
  ownerId: string;
}

/**
 * Candidates whose next action is due today or overdue.
 *
 * An HR sees their own; a Super Admin sees everyone's, with the recruiter named. The backend
 * decides which (getFollowUpsDue scopes an HR to themselves whatever is asked), and sends the
 * same list to the bell each morning, so this and the reminder cannot disagree.
 */
const FollowUpsDue: React.FC = () => {
  const role = useCurrentUserRole();
  // Recruiting work: HR and Super Admin only (the query refuses anyone else).
  if (!isHrRole(role) && !isSuperAdmin(role)) return null;
  return <FollowUpsDueList superAdmin={isSuperAdmin(role)} />;
};

const FollowUpsDueList: React.FC<{ superAdmin: boolean }> = ({ superAdmin }) => {
  const navigate = useNavigate();
  const hrUsers = useHrUsers(superAdmin);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchGraphQLApiData(followUpsDueQuery())
      .then((res) => {
        if (cancelled) return;
        const content = res?.data?.getFollowUpsDue?.content ?? [];
        const today = dayjs().startOf('day');
        setRows(
          content.filter(Boolean).map((a: any) => ({
            id: a._id,
            candidate: candidateDisplayName(a, 'Candidate'),
            job: [a.job?.title, a.job?.companyName].filter(Boolean).join(' · ') || '-',
            status: statusSummary(a.status, a.subStatus),
            nextAction: a.nextAction || '-',
            due: formatDayMonth(a.nextActionAt),
            overdue: !!a.nextActionAt && dayjs(a.nextActionAt).isBefore(today),
            ownerId: a.ownerId || '',
          })),
        );
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // A reminder list that failed to load says so; one that is empty says that. Neither is silent.
  const body = failed ? (
    <EmptyState compact title="Could not load follow-ups" />
  ) : rows == null ? null : rows.length === 0 ? (
    <EmptyState compact title="Nothing due today" body="Set a next action on a candidate and it shows here on the day." />
  ) : (
    <MintlyTable
      rows={rows}
      pagination={{ enabled: false, page: 0, rowsPerPage: rows.length, totalNumberOfRows: rows.length, handleChangePage: () => {} }}
      selectors={superAdmin ? ['candidate', 'job', 'status', 'nextAction', 'due', 'owner'] : ['candidate', 'job', 'status', 'nextAction', 'due']}
      tableHeadRow={{
        candidate: <>Candidate</>,
        job: <>Job</>,
        status: <>Status</>,
        nextAction: <>Next action</>,
        due: <>Due</>,
        owner: <>Recruiter</>,
      }}
      tableBodyRow={{
        candidate: (r: Row) => <>{r.candidate}</>,
        job: (r: Row) => <>{r.job}</>,
        status: (r: Row) => <>{r.status}</>,
        nextAction: (r: Row) => <>{r.nextAction}</>,
        due: (r: Row) => <span className={r.overdue ? 'cc-followups__overdue' : undefined}>{r.overdue ? `${r.due}, overdue` : r.due}</span>,
        owner: (r: Row) => <>{hrUsers.find((u) => u._id === r.ownerId)?.name ?? '-'}</>,
      }}
      onRowClick={(r: Row) => navigate(`/job-applications/${r.id}`)}
    />
  );

  return (
    <Panel className="cc-followups">
      <Box className="cc-followups__header">
        <Typography className="cc-followups__title" variant="subtitle1">
          Follow-ups due{rows && rows.length > 0 ? ` (${rows.length})` : ''}
        </Typography>
        <Typography className="cc-followups__sub" variant="body2">
          {superAdmin ? 'Next actions due today or overdue, for every recruiter' : 'Your candidates with a next action due today or overdue'}
        </Typography>
      </Box>
      {body}
    </Panel>
  );
};

export default FollowUpsDue;
